import { ProjectLostReason } from '@provision/entity-types';
import { gql, QueryHookOptions, useQuery } from '@provision/graphql-client';
import { useMemo } from 'react';
import { ConditionInput, QueryVariables, Records, SortingInput } from 'src/graphql/types';
import { useLoadMore } from 'src/graphql/utils';
import { META_DATA_QUERY_FIELDS } from './metadata.fragments';

const GET_PROJECT_LOST_REASON = gql`
  query GetProjectLostReason($params: QueryParams, $skipTotalRecords: Boolean = false)
  @entity(name: "ProjectLostReason") {
    getProjectLostReason(queryParams: $params) {
      records {
        id
        projectLostReasonId
        name
        createdAt
        updatedAt
      }
      metadata {
        ...MetaDataQueryFields
      }
    }
  }
  ${META_DATA_QUERY_FIELDS}
`;

type GetProjectLostReason = {
  getProjectLostReason: Records<ProjectLostReason>;
};

export const useProjectLostReasons = (params?: {
  filters?: ConditionInput[];
  limit?: number;
  sort?: SortingInput;
  options?: QueryHookOptions<GetProjectLostReason>;
}) => {
  const { data, fetchMore, variables, ...rest } = useQuery<
    GetProjectLostReason,
    QueryVariables
  >(GET_PROJECT_LOST_REASON, {
    ...params?.options,
    variables: {
      skipTotalRecords: true,
      params: {
        filters: params?.filters,
        sorting: params?.sort || { attribute: 'id' },
        limit: params?.limit,
      },
    },
  });

  const projectLostReasons = useMemo(
    () =>
      data?.getProjectLostReason.records.map((i) => ({
        ...i,
        label: i.name,
        value: i.id,
      })) || [],
    [data?.getProjectLostReason.records],
  );

  const { loadMore, hasReachedEnd } = useLoadMore<GetProjectLostReason>({
    offset: projectLostReasons.length,
    fetchMore,
    params: variables?.params!,
  });

  const totalRecords = data?.getProjectLostReason?.metadata?.totalRecords ?? 0;

  return {
    projectLostReasons,
    totalRecords,
    loadMore,
    hasMore: totalRecords ? projectLostReasons?.length < totalRecords : !hasReachedEnd,
    ...rest,
  };
};
